/**
 * Build cubeMembers structure for FieldFilter/OperatorField/ValueField
 * from Cube.js meta response for a specific view.
 *
 * @param {object} metaResponse  - response from useCubeMetaAPI
 * @param {string} viewName      - e.g. "cataloging_view"
 * @param {object} labelsMap     - { fieldKey: translatedLabel }
 * @returns {Array} cubeMembers array compatible with filter components
 */
export const buildCubeMembersFromMeta = (metaResponse, viewName, labelsMap = {}) => {
  if (!metaResponse?.cubes) return [];

  const view = metaResponse.cubes.find(c => c.name === viewName);
  if (!view) return [];

  // Main view cube — use d.name for query, aliasMember for translation
  const mainCube = {
    name: viewName,
    dimensions: (view.dimensions || []).map(d => ({
      name:  d.name,
      type:  d.type,
      label: labelsMap[d.aliasMember] || labelsMap[d.name] || d.shortTitle || d.name,
      aliasMember: d.aliasMember || null,
    })),
    measures: (view.measures || []).map(m => ({
      name:  m.name,
      type:  'number',
      label: labelsMap[m.aliasMember] || labelsMap[m.name] || m.shortTitle || m.name,
      aliasMember: m.aliasMember || null,
    })),
  };

  // Reference table cubes derived from aliasMember namespaces
  const refCubeNames = new Set();
  [...(view.dimensions || []), ...(view.measures || [])].forEach(f => {
    if (f.aliasMember) {
      const ns = f.aliasMember.split('.')[0];
      if (ns !== viewName) refCubeNames.add(ns);
    }
  });

  const refCubes = [];
  refCubeNames.forEach(cubeName => {
    const cube = metaResponse.cubes.find(c => c.name === cubeName);
    if (!cube) return;
    refCubes.push({
      name: cubeName,
      dimensions: (cube.dimensions || []).map(d => ({
        name:  d.name,
        type:  d.type,
        label: labelsMap[d.name] || d.shortTitle || d.name,
      })),
      measures: (cube.measures || []).map(m => ({
        name:  m.name,
        type:  'number',
        label: labelsMap[m.name] || m.shortTitle || m.name,
      })),
      meta: cube.meta || {},
    });
  });

  return [mainCube, ...refCubes];
};

/**
 * Apply a section filter to a list of dimensions/measures from meta.
 *
 * Filter config supports:
 *   { contains: 'holdings' }                    — name includes string
 *   { contains: ['holdings', 'call'] }          — name includes any
 *   { excludeContains: 'item' }                 — name does NOT include
 *   { fieldType: 'measure' }                    — only measures
 *   { fieldType: 'dimension' }                  — only dimensions
 *   null / undefined                            — all fields
 *
 * @param {Array}  fields      - dimensions or measures from meta
 * @param {object} filterCfg   - section filter config
 * @param {string} fieldType   - 'dimension' | 'measure'
 */
export const applyFilter = (fields, filterCfg, fieldType) => {
  if (!fields) return [];

  return fields.filter(f => {
    if (!filterCfg) return true;

    const name = f.name.includes('.')
      ? f.name.split('.').pop()
      : f.name;

    // contains check
    if (filterCfg.contains) {
      const patterns = Array.isArray(filterCfg.contains)
        ? filterCfg.contains
        : [filterCfg.contains];
      if (!patterns.some(p => name.includes(p))) return false;
    }

    // excludeContains check
    if (filterCfg.excludeContains) {
      const excludes = Array.isArray(filterCfg.excludeContains)
        ? filterCfg.excludeContains
        : [filterCfg.excludeContains];
      if (excludes.some(p => name.includes(p))) return false;
    }

    // explicit field list
    if (filterCfg.fields) {
      return filterCfg.fields.includes(f.name);
    }

    return true;
  });
};

/**
 * Resolve timeDimension: converts view field name to its aliasMember.
 * e.g. "cataloging_view.instance_created_date" → "instance.metadata_createddate"
 */
export const resolveTimeDimension = (timeDimension, metaResponse, viewName) => {
  if (!timeDimension || !metaResponse?.cubes) return timeDimension;
  const view = metaResponse.cubes.find(c => c.name === viewName);
  if (!view) return timeDimension;
  const field = [...(view.dimensions || []), ...(view.measures || [])]
    .find(f => f.name === timeDimension);
  return field?.aliasMember || timeDimension;
};

/**
 * Build fieldAliasMap from meta for a specific view.
 * Maps view field names to their aliasMember (cube field key).
 * Used for column header translation in exports and scheduled reports.
 *
 * @param {object} metaResponse
 * @param {string} viewName
 * @returns {object} { "cataloging_view.material_type": "material_type.name", ... }
 */
export const buildFieldAliasMap = (metaResponse, viewName) => {
  if (!metaResponse?.cubes) return {};
  const view = metaResponse.cubes.find(c => c.name === viewName);
  if (!view) return {};

  const map = {};
  [...(view.dimensions || []), ...(view.measures || [])].forEach(f => {
    if (f.aliasMember) map[f.name] = f.aliasMember;
  });
  return map;
};

/**
 * Resolve a column header label using fieldAliasMap then labelsMap.
 * Priority: aliasMember label → direct fieldKey label → fallback
 *
 * @param {string} fieldKey     - view field name or cube field key
 * @param {object} fieldAliasMap - { viewFieldName: aliasMember }
 * @param {object} labelsMap    - { fieldKey: translatedLabel }
 * @returns {string}
 */
export const resolveLabel = (fieldKey, fieldAliasMap, labelsMap) => {
  if (!fieldKey) return '';
  const alias = fieldAliasMap?.[fieldKey];
  if (alias && labelsMap?.[alias]) return labelsMap[alias];
  if (labelsMap?.[fieldKey]) return labelsMap[fieldKey];
  // Fallback: humanize
  const name = fieldKey.includes('.') ? fieldKey.split('.').pop() : fieldKey;
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

/**
 * Resolve a column header label using fieldAliasMap then labelsMap.
 *
 * @param {object} metaResponse
 * @param {string} viewName
 * @param {object} filterCfg     - section filter config
 * @param {string} fieldType     - 'dimension' | 'measure'
 * @param {object} labelsMap
 */
export const buildDataOptions = (metaResponse, viewName, filterCfg, fieldType, labelsMap = {}) => {
  if (!metaResponse?.cubes) return [];

  const view = metaResponse.cubes.find(c => c.name === viewName);
  if (!view) return [];

  const source = fieldType === 'measure'
    ? (view.measures || [])
    : (view.dimensions || []);

  const filtered = applyFilter(source, filterCfg, fieldType);

  return filtered.map(f => ({
    value: f.name,
    label: labelsMap[f.aliasMember] || labelsMap[f.name] || f.shortTitle || f.name,
    aliasMember: f.aliasMember || null,
  }));
};
